import type { ReactNode } from "react";
import { createElement, Fragment } from "react";
import { ChatMentionProductButton } from "./ChatMentionProductButton";

export const CHAT_MENTION_ALL_LABEL = "@所有人";
const CHAT_MENTION_ALL_NAME = "所有人";
export const CHAT_MENTION_CLICK_SELECTOR = ".h5-crm-chat-mention";
export const CHAT_MENTION_PRODUCT_SELECTOR =
  ".h5-crm-chat-mention--product[data-space-product-id]";

export function isChatMentionClickTarget(target: EventTarget | null): boolean {
  const el = eventTargetElement(target);
  return Boolean(el?.closest(CHAT_MENTION_CLICK_SELECTOR));
}

function eventTargetElement(target: EventTarget | null): Element | null {
  if (target instanceof Element) return target;
  if (target instanceof Text) {
    return target.parentElement;
  }
  return null;
}

/** 从点击目标读取 @ 产品（事件委托用） */
export function readChatMentionProductFromTarget(
  target: EventTarget | null
): ChatMentionProduct | null {
  const el = eventTargetElement(target)?.closest<HTMLElement>(CHAT_MENTION_PRODUCT_SELECTOR);
  if (!el) return null;
  const spaceProductId = el.dataset.spaceProductId?.trim() ?? "";
  if (!spaceProductId) return null;
  return {
    spaceProductId,
    displayName: el.dataset.displayName?.trim() ?? "",
    productKind: el.dataset.productKind?.trim() || undefined,
  };
}

export function handleChatMentionTargetClick(
  target: EventTarget | null,
  onProductClick?: (product: ChatMentionProduct) => void
): boolean {
  const product = readChatMentionProductFromTarget(target);
  if (!product || !onProductClick) return false;
  onProductClick(product);
  return true;
}

function mentionProductDataAttrs(product: ChatMentionProduct): Record<string, string> {
  const out: Record<string, string> = {
    "data-space-product-id": product.spaceProductId.trim(),
    "data-display-name": product.displayName.trim(),
  };
  const kind = product.productKind?.trim();
  if (kind) out["data-product-kind"] = kind;
  return out;
}

export interface ChatMentionProduct {
  spaceProductId: string;
  displayName: string;
  productKind?: string;
  /** 额外可匹配名（如 productKind），与 ops @ 插入名对齐 */
  matchNames?: string[];
}

export function toChatMentionProductsFromStartup(
  list: Array<{ spaceProductId?: string; displayName?: string; productKind?: string }>
): ChatMentionProduct[] {
  const out: ChatMentionProduct[] = [];
  for (const row of list) {
    const spaceProductId = row.spaceProductId?.trim() ?? "";
    const displayName = row.displayName?.trim() ?? "";
    if (!spaceProductId || !displayName) continue;
    const productKind = row.productKind?.trim() || undefined;
    const matchNames =
      productKind && productKind !== displayName ? [productKind] : undefined;
    out.push({
      spaceProductId,
      displayName,
      productKind,
      matchNames,
    });
  }
  return out;
}

function collectMatchNames(p: ChatMentionProduct): string[] {
  const names: string[] = [];
  const push = (raw?: string | null): void => {
    const name = raw?.trim();
    if (!name) return;
    if (!names.includes(name)) names.push(name);
  };
  push(p.displayName);
  for (const alias of p.matchNames ?? []) push(alias);
  push(p.productKind);
  return names;
}

function productsForMatch(catalog: ChatMentionProduct[]): ChatMentionProduct[] {
  const seen = new Set<string>();
  const out: ChatMentionProduct[] = [];
  for (const p of catalog) {
    for (const name of collectMatchNames(p)) {
      if (seen.has(name)) continue;
      seen.add(name);
      out.push({ ...p, displayName: name });
    }
  }
  return out.sort((a, b) => b.displayName.length - a.displayName.length);
}

type MentionMatch =
  | { kind: "all"; label: string; length: number }
  | { kind: "product"; product: ChatMentionProduct; label: string; length: number };

function matchMentionAt(text: string, index: number, products: ChatMentionProduct[]): MentionMatch | null {
  if (text[index] !== "@") return null;
  const start = index + 1;
  if (text.startsWith(CHAT_MENTION_ALL_NAME, start)) {
    return { kind: "all", label: CHAT_MENTION_ALL_LABEL, length: 1 + CHAT_MENTION_ALL_NAME.length };
  }
  for (const p of products) {
    const nm = p.displayName.trim();
    if (nm && text.startsWith(nm, start)) {
      return { kind: "product", product: p, label: `@${nm}`, length: 1 + nm.length };
    }
  }
  return null;
}

export function renderChatMentionText(
  text: string | null | undefined,
  catalog: ChatMentionProduct[],
  options?: {
    onProductClick?: (product: ChatMentionProduct) => void;
    renderPlain?: (plain: string) => ReactNode;
  }
): ReactNode {
  const src = String(text ?? "");
  if (!src) return src;
  const products = productsForMatch(catalog);
  const renderPlain = options?.renderPlain ?? ((plain: string) => plain);
  const onProductClick = options?.onProductClick;

  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (let i = 0; i < src.length; ) {
    const matched = matchMentionAt(src, i, products);
    if (matched) {
      if (i > lastIndex) {
        parts.push(createElement(Fragment, { key: `plain-${key++}` }, renderPlain(src.slice(lastIndex, i))));
      }
      if (matched.kind === "all") {
        parts.push(
          createElement(
            "span",
            { key: `mention-${key++}`, className: "h5-crm-chat-mention h5-crm-chat-mention--all" },
            matched.label
          )
        );
      } else if (onProductClick) {
        parts.push(
          createElement(ChatMentionProductButton, {
            key: `mention-${key++}`,
            product: matched.product,
            label: matched.label,
            onActivate: onProductClick,
          })
        );
      } else {
        parts.push(
          createElement(
            "span",
            {
              key: `mention-${key++}`,
              className: "h5-crm-chat-mention h5-crm-chat-mention--product",
              ...mentionProductDataAttrs(matched.product),
            },
            matched.label
          )
        );
      }
      i += matched.length;
      lastIndex = i;
    } else {
      i += 1;
    }
  }

  if (lastIndex < src.length) {
    parts.push(createElement(Fragment, { key: `plain-${key++}` }, renderPlain(src.slice(lastIndex))));
  }

  return parts.length > 0 ? createElement(Fragment, null, ...parts) : src;
}
